<template lang="pug">
button.lang-switcher-button(type="button" :aria-label="switchLabel" @click="toggleLocale")
  span.lang-switcher-window(aria-hidden="true")
    Transition(name="calendar-flip" mode="out-in")
      span.lang-switcher-page(:key="currentLocale") {{ currentLabel }}
</template>

<script lang="ts" setup>
import { useUserPref } from '~/stores/useUserPref'

type LocaleCode = 'en' | 'zh'

const labels: Record<LocaleCode, string> = {
  en: 'EN',
  zh: 'ZH',
}

const userPref = useUserPref()
const { locale, setLocale } = useI18n()

const currentLocale = computed<LocaleCode>(() => {
  return locale.value === 'zh' ? 'zh' : 'en'
})
const targetLocale = computed<LocaleCode>(() => currentLocale.value === 'zh' ? 'en' : 'zh')
const currentLabel = computed(() => labels[currentLocale.value])
const switchLabel = computed(() => `Switch language to ${labels[targetLocale.value]}`)

const mySetLocale = (localeCode: LocaleCode) => {
  setLocale(localeCode)
  userPref.setLocale(localeCode)
}
const toggleLocale = () => {
  mySetLocale(targetLocale.value)
}
onMounted(() => {
  if (userPref.locale) {
    // console.log('userPref.locale.value:', userPref.locale)
    return
  }
  try {
    const userLang = navigator.language?.startsWith('zh') ? 'zh' : 'en'
    mySetLocale(userLang)
  } catch (e) {
    console.log('setting default lang failed.', e)
  }
})
</script>

<style lang="postcss" scoped>
.lang-switcher-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.55rem;
  height: 1.75rem;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: var(--lang-switcher-color, inherit);
  font: inherit;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  line-height: 1;
  text-transform: uppercase;
  cursor: pointer;
  outline: none;
  box-shadow: none;
  perspective: 7rem;

  &:hover {
    color: var(--lang-switcher-hover-color, #7ed321);
  }

  &:active .lang-switcher-window {
    transform: translateY(1px);
  }

  &:focus-visible {
    border-radius: 4px;
    outline: 1px solid var(--lang-switcher-focus-color, rgba(126, 211, 33, 0.72));
    outline-offset: 3px;
  }

  @media (max-width: 768px) {
    width: 2.35rem;
    height: 1.65rem;
    font-size: 0.64rem;
  }
}

.lang-switcher-window {
  position: relative;
  display: inline-grid;
  place-items: center;
  width: 100%;
  height: 100%;
  overflow: hidden;
  transform-style: preserve-3d;
  transition: transform 0.12s cubic-bezier(0.16, 1, 0.3, 1);
}

.lang-switcher-page {
  grid-area: 1 / 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  backface-visibility: hidden;
  transform-origin: 50% 0%;
  will-change: transform, opacity;
}

.calendar-flip-enter-active,
.calendar-flip-leave-active {
  transition:
    opacity 0.22s cubic-bezier(0.16, 1, 0.3, 1),
    transform 0.22s cubic-bezier(0.16, 1, 0.3, 1);
}

.calendar-flip-enter-from {
  opacity: 0;
  transform: translateY(0.55rem) rotateX(-82deg);
}

.calendar-flip-leave-to {
  opacity: 0;
  transform: translateY(-0.55rem) rotateX(82deg);
}

@media (prefers-reduced-motion: reduce) {
  .lang-switcher-window,
  .calendar-flip-enter-active,
  .calendar-flip-leave-active {
    transition-duration: 0.01ms;
  }
}
</style>
